import { useSelector } from 'react-redux';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
  Divider,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
} from '@mui/material';
import noop from 'lodash/noop';

import { SidebarSecondaryNav } from './SidebarSecondaryNav';
import ProtectedComponentBase from '../../../components/main/protected/ProtectedComponent';
import ActivityIcon from '../../../_assets/svg/Activity.svg';
import ApikeysIcon from '../../../_assets/svg/Apikeys.svg';
import ApidocsIcon from '../../../_assets/svg/Apidocs.svg';
import CowbellIcon from '../../../_assets/svg/Cowbell.svg';
import SupportSimpleIcon from '../../../_assets/svg/SupportSimple.svg';
import { usePersona } from '../../../components/hooks/usePersona';
import {
  useIconStyles,
  useItemStyles,
  useItemTextStyles,
  useSecondaryNavIcon,
} from './styles';
import { withShowable } from '../lib/withShowable';
import { useCustomerRoutes } from '../../customers/customer.routes';
import { RenderMuiListItem } from './RenderMuiListItem';
import { RailTooltip } from './RailTooltip';

const ProtectedComponent = withShowable(ProtectedComponentBase);

export function BottomNavigation({
  location,
  rootPath,
  handleClick,
  support,
  showDocumentRoute = false,
  showManageRoute = false,
  railCollapsed = false,
}: any) {
  const itemClasses = useItemStyles();
  const itemIconClasses = useIconStyles();
  const itemTextClasses = useItemTextStyles();
  const secondaryIconClasses = useSecondaryNavIcon();
  const persona = usePersona();
  const { version } = useSelector((state: any) => state.uiSettings);
  const routes = useCustomerRoutes();
  const documentRoute = routes?.find(
    (route) => route.nav?.name === 'Documents'
  );
  const manageRoute = routes?.find((route) => route.nav?.name === 'Manage');

  const splitTab = location.pathname
    .split('/')
    .slice(0, persona.isInsured ? 4 : 3);
  const finalURL = splitTab.join('/').split('/:').shift();

  return (
    <div
      className="small"
      style={{ marginBottom: '0.8333333333rem', paddingBottom: '1rem' }}
    >
      <Divider style={{ margin: '1rem auto', width: '80%' }} />
      {!persona.isVendor && (
        <>
          {showDocumentRoute && documentRoute && documentRoute.nav && (
            <RenderMuiListItem
              route={documentRoute}
              to={documentRoute.path}
              finalURL={finalURL}
              handleClick={noop}
              railCollapsed={railCollapsed}
              key={documentRoute.path}
            />
          )}
          {showManageRoute && manageRoute && manageRoute.nav && (
            <RenderMuiListItem
              route={manageRoute}
              to={manageRoute.path}
              finalURL={finalURL}
              handleClick={noop}
              railCollapsed={railCollapsed}
              key={manageRoute.path}
            />
          )}
          <ProtectedComponent allow={{ audit_log: 'view' }}>
            <RailTooltip railCollapsed={railCollapsed} title="Activity">
              <MuiListItem
                button
                classes={itemClasses}
                selected={location.pathname === `/${rootPath}/activity`}
                component={Link}
                to={`/${rootPath}/activity`}
                onClick={handleClick}
                data-qa="SIDEBAR_NAVLINK:Activity"
              >
                <MuiListItemIcon classes={itemIconClasses}>
                  <ActivityIcon viewbox="0 0 20 20" />
                </MuiListItemIcon>
                <MuiListItemText
                  classes={itemTextClasses}
                  disableTypography
                  secondary="Activity"
                />
              </MuiListItem>
            </RailTooltip>
          </ProtectedComponent>
          <ProtectedComponent allow={{ api_keys: 'view' }}>
            {!persona.isPool && (
              <RailTooltip railCollapsed={railCollapsed} title="Developers">
                <MuiListItem
                  button
                  classes={itemClasses}
                  selected={location.pathname === `/${rootPath}/developers`}
                  component={Link}
                  to={`/${rootPath}/developers/api-keys`}
                  onClick={handleClick}
                  data-qa="SIDEBAR_NAVLINK:Developers"
                >
                  <MuiListItemIcon classes={itemIconClasses}>
                    <ApikeysIcon viewbox="0 0 20 20" />
                  </MuiListItemIcon>
                  <MuiListItemText
                    classes={itemTextClasses}
                    disableTypography
                    secondary="Developers"
                  />
                </MuiListItem>
              </RailTooltip>
            )}
          </ProtectedComponent>
          {!persona.isPool && (
            <RailTooltip railCollapsed={railCollapsed} title="API Docs">
              <MuiListItem
                button
                classes={itemClasses}
                component="a"
                href="https://developers.cowbellcyber.ai"
                onClick={handleClick}
                target="_blank"
                data-qa="SIDEBAR_NAVLINK:Api-docs"
              >
                <MuiListItemIcon classes={itemIconClasses}>
                  <ApidocsIcon viewbox="0 0 20 20" />
                </MuiListItemIcon>
                <MuiListItemText
                  classes={itemTextClasses}
                  disableTypography
                  secondary="API Docs"
                />
              </MuiListItem>
            </RailTooltip>
          )}
        </>
      )}
      {support.subroutes ? (
        <ProtectedComponent
          allow={support.allow}
          allowEither={support.allowEither}
          allowIfAll={support.allowIfAll}
        >
          <SidebarSecondaryNav
            routes={support.subroutes}
            parent="Support"
            railCollapsed={railCollapsed}
          >
            <MuiListItem
              data-submenu
              button
              classes={itemClasses}
              component="a"
              selected={location.pathname.startsWith(`/${rootPath}/support`)}
              data-qa="SIDEBAR_NAVLINK:Support"
            >
              <MuiListItemIcon classes={itemIconClasses}>
                <SupportSimpleIcon viewbox="0 0 20 20" />
              </MuiListItemIcon>
              <MuiListItemText
                classes={itemTextClasses}
                disableTypography
                secondary="Support"
              />
              <ChevronRightIcon classes={secondaryIconClasses} />
            </MuiListItem>
          </SidebarSecondaryNav>
        </ProtectedComponent>
      ) : (
        <RailTooltip railCollapsed={railCollapsed} title="Support">
          <MuiListItem
            button
            classes={itemClasses}
            selected={location.pathname === `/${rootPath}/support`}
            component={Link}
            to={`/${rootPath}/support`}
            onClick={handleClick}
            data-qa="SIDEBAR_NAVLINK:Support"
          >
            <MuiListItemIcon classes={itemIconClasses}>
              <SupportSimpleIcon viewbox="0 0 20 20" />
            </MuiListItemIcon>
            <MuiListItemText
              classes={itemTextClasses}
              disableTypography
              secondary="Support"
            />
          </MuiListItem>
        </RailTooltip>
      )}
      <RailTooltip railCollapsed={railCollapsed} title={`V ${version.v}`}>
        <MuiListItem classes={itemClasses} data-qa="SIDEBAR_NAVLINK:Version">
          <MuiListItemIcon classes={itemIconClasses}>
            <CowbellIcon viewbox="0 0 20 20" />
          </MuiListItemIcon>
          <MuiListItemText
            classes={itemTextClasses}
            disableTypography
            secondary={`V ${version.v}`}
          />
        </MuiListItem>
      </RailTooltip>
    </div>
  );
}
